import { useState } from 'react';
import { Button } from './ui/Button';
import AuthDivider from './auth/AuthDivider';
import SocialProviderIcon from './auth/SocialProviderIcon';
import { SOCIAL_PROVIDER_NAMES, SocialProvider } from '../services/authProviders';
import { useSignInMethods } from '../hooks/useSignInMethods';
import { useAuthStore } from '../store/authStore';

interface SocialLoginProps {
    onLoginSuccess?: () => void;
    variant?: 'login' | 'register';
    /** Replaces the default store sign-in, e.g. to link a provider onto a guest account. */
    onSelect?: (provider: SocialProvider) => Promise<void>;
    /** The "Or sign in with" divider, which only makes sense below another way to sign in. */
    divider?: boolean;
}

/** "Google", "Google or X", "Google, X, or Microsoft". */
function listProviderNames(names: string[]): string {
    if (names.length <= 2) return names.join(' or ');
    return `${names.slice(0, -1).join(', ')}, or ${names[names.length - 1]}`;
}

export default function SocialLogin({ onLoginSuccess, variant = 'login', onSelect, divider = true }: SocialLoginProps) {
    const [pending, setPending] = useState<SocialProvider | null>(null);
    const { socialLogin, clearError } = useAuthStore();
    const providers = (useSignInMethods()?.social ?? []) as SocialProvider[];

    const handleSelect = async (provider: SocialProvider) => {
        if (pending) return;

        setPending(provider);
        clearError();

        try {
            await (onSelect ?? socialLogin)(provider);
            onLoginSuccess?.();
        } catch {
            // Surfaced by the store (or by onSelect's caller).
        } finally {
            setPending(null);
        }
    };

    if (providers.length === 0) {
        return null;
    }

    const verb = variant === 'login' ? 'Sign in' : 'Sign up';

    return (
        <div className="w-full">
            {divider && <AuthDivider label={variant === 'login' ? 'Or sign in with' : 'Or sign up with'} />}

            {/* Icon-only, one shared style: five full-width labelled buttons pushed the
                email form off the first screen. "Sign in with X" stays as each button's
                accessible name and tooltip, and the caption below names them all for
                touch screens, where tooltips never show. */}
            {/* Tiles flex between 44px and 56px and only wrap when 44px no longer fits,
                so a narrow phone never gets a sub-44px tap target. */}
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3">
                {providers.map((provider) => {
                    const label = `${verb} with ${SOCIAL_PROVIDER_NAMES[provider]}`;
                    return (
                        <Button
                            key={provider}
                            type="button"
                            aria-label={label}
                            title={label}
                            onClick={() => handleSelect(provider)}
                            disabled={pending !== null}
                            loading={pending === provider}
                            variant="secondary"
                            size="lg"
                            leadingIcon={<SocialProviderIcon provider={provider} />}
                            className="min-w-11 max-w-14 flex-1 px-0"
                        />
                    );
                })}
            </div>
            <p aria-hidden="true" className="mt-2 text-center text-content-muted text-micro">
                {listProviderNames(providers.map((provider) => SOCIAL_PROVIDER_NAMES[provider]))}
            </p>
        </div>
    );
}
